import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  Alert,
  Skeleton,
  Divider,
} from '@mui/material';
import {
  InfoOutlined as InfoIcon,
  DeleteOutline as DeleteIcon,
  FileDownload as LoadIcon,
  CheckCircle as CheckIcon,
  NoteAdd as NoteAddIcon,
} from '@mui/icons-material';
import { SchemaData } from '../../pages/ExcelUploadPage';

export interface SchemaLoaderProps {
  schemas: SchemaData[];
  /** Name of the schema currently applied to the mapping */
  loadedSchemaName?: string;
  onLoad: (schema: SchemaData) => void;
  /** Called when user explicitly selects "New mapping" (clear selection) */
  onClear?: () => void;
  onDelete?: (schema: SchemaData) => Promise<void>;
  title?: string;
  subtitle?: string;
  emptyMessage?: string;
  loading?: boolean;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function getMappingCount(mappings: Record<string, string>): number {
  return Object.keys(mappings).filter(k => mappings[k]).length;
}

function inferLegCount(schema: SchemaData): number | null {
  if (schema.legCount) return schema.legCount;
  const nums = Object.values(schema.mappings)
    .map(f => {
      const m = /_leg(\d+)$/.exec(f);
      return m ? Number.parseInt(m[1], 10) : 0;
    })
    .filter(n => n > 0);
  return nums.length > 0 ? Math.max(...nums) : null;
}

/** Order mapping keys: numeric column keys ascending, then MANUAL: keys alphabetically. */
function compareMappingKeys(a: string, b: string): number {
  const aIsManual = a.startsWith('MANUAL:');
  const bIsManual = b.startsWith('MANUAL:');
  if (aIsManual && !bIsManual) return 1;
  if (!aIsManual && bIsManual) return -1;
  if (aIsManual && bIsManual) return a.localeCompare(b);
  return Number.parseInt(a, 10) - Number.parseInt(b, 10);
}

/** Resolve the select key for the schema matching loadedSchemaName, or '' if none. */
function resolveSelectedKey(loadedSchemaName: string | undefined, schemas: SchemaData[]): string {
  if (!loadedSchemaName) return '';
  const match = schemas.find(s => s.name === loadedSchemaName);
  return match ? (match.id ?? match.name) : '';
}

function buildMeta(s: SchemaData): string {
  const legCount = inferLegCount(s);
  const updatedAt = (s as any).updatedAt || (s as any).lastModifiedDate;
  const updatedByName = (s as any).updatedByName || (s as any).lastModifiedBy;
  const updatedByType = (s as any).updatedByType;
  const updatedByTypeSuffix = updatedByType ? ` (${updatedByType})` : '';
  return [
    `${getMappingCount(s.mappings)} fields`,
    legCount ? `${legCount}L` : null,
    s.settings.standard,
    updatedAt ? formatDate(updatedAt) : null,
    updatedByName ? `by ${updatedByName}` + updatedByTypeSuffix : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Two-line dropdown row content (name + badges + metadata) for a single schema option. */
function SchemaMenuItemContent({ schema, isActive }: Readonly<{ schema: SchemaData; isActive: boolean }>) {
  const legCount = inferLegCount(schema);
  const fieldCount = getMappingCount(schema.mappings);
  const updatedAt = (schema as any).updatedAt || (schema as any).lastModifiedDate;
  const updatedByName = (schema as any).updatedByName || (schema as any).lastModifiedBy;
  const updatedByType = (schema as any).updatedByType;
  return (
    <Box sx={{ width: '100%' }}>
      {/* Row 1: name + badges */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.4 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
          {schema.name}
        </Typography>
        {isActive && (
          <Chip
            label="Active"
            size="small"
            color="primary"
            sx={{ height: 17, fontSize: '0.62rem', fontWeight: 700 }}
          />
        )}
        {legCount && (
          <Chip
            label={`${legCount}L`}
            size="small"
            variant="outlined"
            sx={{
              height: 17,
              fontSize: '0.62rem',
              borderColor: 'grey.300',
              color: 'text.secondary',
            }}
          />
        )}
      </Box>

      {/* Row 2: metadata */}
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
          {fieldCount} field{fieldCount === 1 ? '' : 's'} mapped
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
          {schema.settings.standard}
        </Typography>
        {updatedAt && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
            {formatDate(updatedAt)}
          </Typography>
        )}
        {updatedByName && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
            by {updatedByName}{updatedByType ? ` (${updatedByType})` : ''}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/** Fixed-height two-line display — same structure used for both filled and empty states */
function SchemaSelectValue({ s }: Readonly<{ s: SchemaData | null }>) {
  return (
    <Box sx={{ lineHeight: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
        {s ? s.name : '\u00A0'}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
        {s ? buildMeta(s) : '\u00A0'}
      </Typography>
    </Box>
  );
}

/** Read-only details dialog for a single schema (settings, history, column mappings). */
function SchemaDetailDialog({
  schema,
  open,
  onClose,
  isLoaded,
  onLoad,
}: Readonly<{
  schema: SchemaData;
  open: boolean;
  onClose: () => void;
  isLoaded: boolean;
  onLoad: () => void;
}>) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {schema.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Schema details
        </Typography>
      </DialogTitle>

      <Divider sx={{ mx: 3, mt: 1.5 }} />

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {/* Settings */}
        <Typography
          variant="overline"
          sx={{ fontSize: '0.62rem', letterSpacing: 1.5, color: 'text.secondary', display: 'block', mb: 1 }}
        >
          Processing Settings
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2.5 }}>
          <Chip
            label={schema.settings.hasHeader ? 'Header row' : 'No header row'}
            size="small"
            variant="outlined"
            color={schema.settings.hasHeader ? 'success' : 'default'}
          />
          <Chip
            label={`${schema.settings.standard} standard`}
            size="small"
            variant="outlined"
            color="primary"
          />
          <Chip
            label={schema.settings.useStreetDistances ? 'Street distances' : 'Direct distances'}
            size="small"
            variant="outlined"
          />
          {schema.legCount && (
            <Chip
              label={`${schema.legCount} leg${schema.legCount > 1 ? 's' : ''}`}
              size="small"
              variant="outlined"
            />
          )}
          {schema.mappingType && (
            <Chip
              label={schema.mappingType === 'inline' ? 'Inline' : 'Traditional'}
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        {/* History */}
        {(((schema as any).updatedAt || (schema as any).lastModifiedDate) || (schema as any).createdAt) && (
          <>
            <Typography
              variant="overline"
              sx={{ fontSize: '0.62rem', letterSpacing: 1.5, color: 'text.secondary', display: 'block', mb: 1 }}
            >
              History
            </Typography>
            <Box
              sx={{
                mb: 2.5,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'grey.200',
                overflow: 'hidden',
              }}
            >
              {((schema as any).updatedAt || (schema as any).lastModifiedDate) && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1,
                    bgcolor: 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: 'grey.100',
                    gap: 2,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Last modified
                  </Typography>
                  <Typography variant="caption" sx={{ textAlign: 'right' }}>
                    {formatDate((schema as any).updatedAt || (schema as any).lastModifiedDate)}
                  </Typography>
                </Box>
              )}
              {((schema as any).updatedByName || (schema as any).lastModifiedBy) && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1,
                    bgcolor: 'grey.50',
                    borderBottom: (schema as any).createdAt ? '1px solid' : 'none',
                    borderColor: 'grey.100',
                    gap: 2,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Last modified by
                  </Typography>
                  <Typography variant="caption" sx={{ textAlign: 'right', fontWeight: 600 }}>
                    {(schema as any).updatedByName || (schema as any).lastModifiedBy}
                    {(schema as any).updatedByType ? ` (${(schema as any).updatedByType})` : ''}
                  </Typography>
                </Box>
              )}
              {(schema as any).createdAt && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1,
                    gap: 2,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Created
                  </Typography>
                  <Typography variant="caption" sx={{ textAlign: 'right' }}>
                    {formatDate((schema as any).createdAt)}
                    {(schema as any).createdByName ? ` · ${(schema as any).createdByName}` : ''}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}

        {/* Column Mappings */}
        <Typography
          variant="overline"
          sx={{ fontSize: '0.62rem', letterSpacing: 1.5, color: 'text.secondary', display: 'block', mb: 1 }}
        >
          Column Mappings ({getMappingCount(schema.mappings)} fields)
        </Typography>
        <Box
          sx={{
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'grey.200',
            overflow: 'hidden',
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {Object.entries(schema.mappings)
            .filter(([, v]) => v)
            .sort(([a], [b]) => compareMappingKeys(a, b))
            .map(([key, value], idx, arr) => {
              const isManual = key.startsWith('MANUAL:');
              const colDisplay = isManual
                ? `Manual: ${key.replace('MANUAL:', '')}`
                : `Column ${Number.parseInt(key, 10) + 1}`;
              return (
                <Box
                  key={key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1,
                    gap: 1.5,
                    bgcolor: idx % 2 === 0 ? 'grey.50' : 'background.paper',
                    borderBottom: idx < arr.length - 1 ? '1px solid' : 'none',
                    borderColor: 'grey.100',
                  }}
                >
                  {/* Column index badge */}
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      color: isManual ? 'info.main' : 'grey.500',
                      bgcolor: isManual ? 'info.50' : 'grey.200',
                      borderRadius: 0.75,
                      px: 0.6,
                      py: 0.1,
                      flexShrink: 0,
                      lineHeight: '1.4',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isManual ? 'M' : `#${Number.parseInt(key, 10) + 1}`}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      flex: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.7rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {colDisplay}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'grey.400', flexShrink: 0 }}>
                    →
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      flex: 1,
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      color: 'primary.dark',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {value
                      .replace(/_leg(\d+)$/i, ' Leg $1')
                      .replaceAll('_', ' ')
                      .replaceAll(/\b\w/g, c => c.toUpperCase())}
                  </Typography>
                </Box>
              );
            })}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button size="small" onClick={onClose} color="inherit">
          Close
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => { onLoad(); onClose(); }}
          disabled={isLoaded}
          startIcon={isLoaded ? <CheckIcon /> : <LoadIcon />}
        >
          {isLoaded ? 'Applied' : 'Load Schema'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Premium schema loader. Layout: [Select ──────] [ℹ] [🗑] [Load Schema →]
 * Selecting does not auto-apply; the user must click "Load Schema".
 */
const SchemaLoader: React.FC<SchemaLoaderProps> = ({
  schemas,
  loadedSchemaName,
  onLoad,
  onClear,
  onDelete,
  title = 'Load a Saved Schema',
  subtitle = 'Apply a saved column mapping template to speed up your workflow.',
  emptyMessage = 'No saved schemas yet. After completing a mapping, save it as a schema to reuse it here.',
  loading = false,
}) => {
  const [selectedKey, setSelectedKey] = useState<string>(() =>
    resolveSelectedKey(loadedSchemaName, schemas)
  );
  const [detailOpen, setDetailOpen] = useState(false);

  // Keep selectedKey in sync when loadedSchemaName changes from outside (e.g. navigating back)
  useEffect(() => {
    setSelectedKey(resolveSelectedKey(loadedSchemaName, schemas));
  }, [loadedSchemaName, schemas]);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLElement | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const selectedSchema =
    schemas.find(s => (s.id ?? s.name) === selectedKey) ?? null;

  const isLoaded = !!selectedSchema && loadedSchemaName === selectedSchema.name;

  const handleLoad = () => {
    if (selectedSchema) onLoad(selectedSchema);
  };

  const handleSelectChange = (key: string) => {
    setSelectedKey(key);
    if (!key) {
      onClear?.();
      return;
    }
    const s = schemas.find(s => (s.id ?? s.name) === key);
    if (s) onLoad(s);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSchema || !onDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await onDelete(selectedSchema);
      setDeleteAnchorEl(null);
      setSelectedKey('');
    } catch (e: any) {
      setDeleteError(e.message || 'Failed to delete schema. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={140} height={20} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width={260} height={18} sx={{ mb: 1.5 }} />
        <Skeleton variant="rounded" height={36} />
      </Box>
    );
  }

  // ── Empty state ───────────────────────────────────────────────────────────
  if (schemas.length === 0) {
    return (
      <Box
        sx={{
          py: 2,
          px: 2.5,
          borderRadius: 1.5,
          bgcolor: 'grey.50',
          border: '1px dashed',
          borderColor: 'grey.300',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  // ── Main ──────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Header — hidden when title is empty (caller renders its own header) */}
      {title && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {subtitle && (
            <Tooltip title={subtitle} arrow>
              <InfoIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'default' }} />
            </Tooltip>
          )}
        </Box>
      )}

      {/* Inline control row: [Select] [ℹ] [🗑] [Load Schema] */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Dropdown */}
        <FormControl size="small" sx={{ flex: 1, minWidth: 0, mb: 0 }}>
          <InputLabel shrink>Choose a schema</InputLabel>
          <Select
            value={selectedKey}
            label="Choose a schema"
            displayEmpty
            notched
            onChange={e => handleSelectChange(e.target.value)}
            renderValue={value => {
              if (!value) {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NoteAddIcon sx={{ fontSize: 15, color: 'text.disabled', flexShrink: 0 }} />
                    <Box sx={{ lineHeight: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, color: 'text.disabled' }}>
                        New mapping
                      </Typography>
                      <Typography variant="caption" sx={{ lineHeight: 1.3, color: 'text.disabled' }}>
                        Build column mapping from scratch
                      </Typography>
                    </Box>
                  </Box>
                );
              }
              const s = schemas.find(s => (s.id ?? s.name) === value) ?? null;
              return <SchemaSelectValue s={s} />;
            }}
            slotProps={{ paper: { sx: { maxHeight: 400 } } } as any}
          >
            {/* Sentinel — "New mapping / start fresh" */}
            <MenuItem
              value=""
              sx={{
                py: 1.25,
                px: 1.5,
                mx: 0.75,
                mt: 0.5,
                mb: 0.25,
                borderRadius: 1.5,
                border: '1.5px dashed',
                borderColor: 'grey.300',
                '&:hover': { bgcolor: 'primary.50', borderColor: 'primary.300' },
                '&.Mui-selected': { bgcolor: 'primary.50', borderColor: 'primary.main', borderStyle: 'solid' },
                '&.Mui-selected:hover': { bgcolor: 'primary.100' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: 1,
                    border: '1.5px dashed',
                    borderColor: 'grey.400',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    bgcolor: 'grey.50',
                    '.Mui-selected &': { borderColor: 'primary.main', bgcolor: 'primary.50' },
                  }}
                >
                  <NoteAddIcon sx={{ fontSize: 15, color: 'text.secondary', '.Mui-selected &': { color: 'primary.main' } }} />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    New mapping
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                    Start from scratch — no pre-filled template
                  </Typography>
                </Box>
              </Box>
            </MenuItem>

            <Divider sx={{ my: 0.75, mx: 1 }} />

            {schemas.map(schema => {
              const key = schema.id ?? schema.name;
              return (
                <MenuItem
                  key={key}
                  value={key}
                  sx={{
                    py: 1.25,
                    px: 2,
                    '&.Mui-selected': { bgcolor: 'primary.50' },
                    '&.Mui-selected:hover': { bgcolor: 'primary.100' },
                  }}
                >
                  <SchemaMenuItemContent schema={schema} isActive={loadedSchemaName === schema.name} />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        {/* Info button */}
        <Tooltip title={selectedSchema ? 'View schema details' : 'Select a schema first'}>
          <span style={{ display: 'flex' }}>
            <IconButton
              size="small"
              onClick={() => setDetailOpen(true)}
              disabled={!selectedSchema}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'primary.main', bgcolor: 'primary.50' },
                '&.Mui-disabled': { opacity: 0.35 },
              }}
            >
              <InfoIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>

        {/* Delete button */}
        {onDelete && (
          <Tooltip title={selectedSchema ? 'Delete this schema' : 'Select a schema first'}>
            <span style={{ display: 'flex' }}>
              <IconButton
                size="small"
                onClick={e => setDeleteAnchorEl(e.currentTarget)}
                disabled={!selectedSchema}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'error.main', bgcolor: 'error.50' },
                  '&.Mui-disabled': { opacity: 0.35 },
                }}
              >
                <DeleteIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}

      </Box>

      {/* Loaded confirmation hint */}
      {isLoaded && selectedSchema && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
          <CheckIcon sx={{ fontSize: 13, color: 'primary.main' }} />
          <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
            "{selectedSchema.name}" is applied to the current mapping
          </Typography>
        </Box>
      )}

      {/* ── Delete Confirmation Popover ─────────────────────────────────── */}
      <Popover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={() => { setDeleteAnchorEl(null); setDeleteError(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { borderRadius: 2, boxShadow: 4 } } } as any}
      >
        <Box sx={{ p: 2.5, maxWidth: 280 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Delete schema?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
            Permanently delete{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              "{selectedSchema?.name}"
            </Box>
            . This cannot be undone.
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 1.5, py: 0.5, fontSize: '0.75rem' }}>
              {deleteError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => setDeleteAnchorEl(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* ── Schema Detail Dialog ────────────────────────────────────────── */}
      {selectedSchema && (
        <SchemaDetailDialog
          schema={selectedSchema}
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          isLoaded={isLoaded}
          onLoad={handleLoad}
        />
      )}
    </Box>
  );
};

export default SchemaLoader;
